/**
 * 微信分享卡片（JS-SDK）。
 *
 * 只在微信内置浏览器里生效：用户点右上角「…」→ 发送给朋友 / 分享到朋友圈时，
 * 显示我们设置的标题、描述、封面。直接复制链接粘贴到聊天框永远是纯链接，这是微信的限制。
 *
 * 前置条件：公众号后台「JS 接口安全域名」已配 www.100apple.net。
 *
 * 失败全部静默：分享卡片是锦上添花，拿不到签名就退化成微信默认的链接样式，不打扰用户。
 */
import request from '@/api/request'

interface JsConfig {
  appId: string
  timestamp: number
  nonceStr: string
  signature: string
}

export interface ShareData {
  title: string
  desc: string
  /** 可为空，空时用站点 logo */
  imgUrl?: string
  /** 可为空，空时用当前页（去掉 #） */
  link?: string
}

interface WxSdk {
  config(c: Record<string, unknown>): void
  ready(fn: () => void): void
  error(fn: (e: unknown) => void): void
  updateAppMessageShareData(d: Record<string, unknown>): void
  updateTimelineShareData(d: Record<string, unknown>): void
  // 旧接口（已标记废弃但仍可用）：部分安卓微信版本新接口不生效时用它兜底
  onMenuShareAppMessage?(d: Record<string, unknown>): void
  onMenuShareTimeline?(d: Record<string, unknown>): void
}

/** 每次 setWechatShare 自增；异步回来时不是最新一次就丢弃，避免旧页面的卡片覆盖新页面 */
let shareSeq = 0

const SDK_URL = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js'
const DEFAULT_TITLE = '百果量化论坛'
const DEFAULT_DESC = '量化交易策略、PTrade 源码与实盘经验交流社区'

export const inWechat = /micromessenger/i.test(navigator.userAgent)
const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent)

/**
 * iOS 微信的坑：SPA 里用 history 路由切页后，微信认的“当前 URL”仍是【首次进入】的地址，
 * 签名必须用那个，否则 invalid signature。Android 用当前地址。
 * 模块首次加载（即页面首次打开）时记下来。
 */
const landingUrl = location.href.split('#')[0]

let sdkPromise: Promise<WxSdk | null> | null = null

function loadSdk(): Promise<WxSdk | null> {
  if (sdkPromise) return sdkPromise
  sdkPromise = new Promise((resolve) => {
    const w = window as unknown as { wx?: WxSdk }
    if (w.wx) return resolve(w.wx)
    const s = document.createElement('script')
    s.src = SDK_URL
    s.async = true
    s.onload = () => resolve(w.wx ?? null)
    s.onerror = () => {
      sdkPromise = null // 允许下次重试
      resolve(null)
    }
    document.head.appendChild(s)
  })
  return sdkPromise
}

/**
 * 默认分享图（绝对地址）。微信分享卡片不支持 SVG，必须是 png/jpg，
 * 放在 forum-web/public/share-logo.png（建议 300x300 以上正方形）。
 */
function defaultImg(): string {
  return `${location.origin}/share-logo.png`
}

/** 相对路径转绝对（封面可能是 /storage/... 相对地址） */
function absolute(url: string | undefined): string {
  if (!url) return defaultImg()
  if (/^https?:\/\//i.test(url)) return url.replace(/^http:\/\//i, 'https://')
  return `${location.origin}${url.startsWith('/') ? '' : '/'}${url}`
}

/**
 * 设置当前页的分享卡片。非微信环境直接返回。
 *
 * 每次切到新文章都要调一次（路由变了，签名 URL 在 Android 上也变了）。
 */
export async function setWechatShare(data: ShareData): Promise<void> {
  if (!inWechat) return
  const seq = ++shareSeq
  const debug = /[?&]wxdebug=1/.test(location.search)
  const log = (msg: string) => {
    if (debug) alert(msg)
  }
  try {
    const wx = await loadSdk()
    if (!wx || seq !== shareSeq) return

    const signUrl = isIOS ? landingUrl : location.href.split('#')[0]
    const cfg = await request.get<unknown, JsConfig | null>('/api/web/wechat/js-config', {
      params: { url: signUrl },
    })
    // 签名失败时后端返回 data=null（不报业务错），拦截器不会弹 toast
    if (!cfg) {
      log('签名接口返回空，看后端日志「JS-SDK 签名失败」')
      return
    }
    if (seq !== shareSeq) return

    wx.config({
      // 排查用：链接后加 ?wxdebug=1，微信会弹窗显示每个接口的调用结果
      debug,
      appId: cfg.appId,
      timestamp: cfg.timestamp,
      nonceStr: cfg.nonceStr,
      signature: cfg.signature,
      jsApiList: [
        'updateAppMessageShareData',
        'updateTimelineShareData',
        'onMenuShareAppMessage',
        'onMenuShareTimeline',
      ],
    })

    // 分享链接去掉排查参数，别让收到卡片的人也弹调试框
    const cleanLink = (data.link || location.href.split('#')[0])
      .replace(/([?&])wxdebug=1(&|$)/, (_m, p1, p2) => (p2 ? p1 : ''))
      .replace(/[?&]$/, '')
    const payload = {
      title: (data.title || DEFAULT_TITLE).slice(0, 64),
      desc: (data.desc || DEFAULT_DESC).slice(0, 120),
      link: cleanLink,
      imgUrl: absolute(data.imgUrl),
    }
    wx.ready(() => {
      if (seq !== shareSeq) return
      // 新接口。它只是"设置"，要等用户点转发才生效；设置失败时 fail 会带原因
      wx.updateAppMessageShareData({
        ...payload,
        success: () => log('好友卡片设置成功：' + payload.title),
        fail: (e: unknown) => log('好友卡片设置失败：' + JSON.stringify(e)),
      })
      wx.updateTimelineShareData({
        title: payload.title,
        link: payload.link,
        imgUrl: payload.imgUrl,
      })
      // 旧接口兜底：部分安卓微信版本上新接口返回 ok 但转发时不生效，旧接口仍然有效
      wx.onMenuShareAppMessage?.({ ...payload })
      wx.onMenuShareTimeline?.({ title: payload.title, link: payload.link, imgUrl: payload.imgUrl })
    })
    wx.error((e: unknown) => {
      log('wx.config 失败：' + JSON.stringify(e))
    })
  } catch (e) {
    log('分享设置异常：' + String(e))
  }
}

/** 恢复成站点默认卡片（离开文章页时用） */
export function setDefaultWechatShare(): Promise<void> {
  return setWechatShare({ title: DEFAULT_TITLE, desc: DEFAULT_DESC })
}
